import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Tabs, Tab, Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { langs, createLanguageObject } from '@utils/helpers';
import MergeFieldHelp from '@components/workflow/messagedialog/MergeFieldHelp';

const SmsContent = forwardRef(({ initialContent = '', onValidityChange }, ref) => {
    const { t } = useTranslation();
    const [currentTab, setCurrentTab] = useState(0);
    const [content, setContent] = useState(createLanguageObject());
    const [charCount, setCharCount] = useState(createLanguageObject());
    const SMS_CHAR_LIMIT = 612;

    useEffect(() => {
        if (initialContent) {
            try {
                const parsedContent = JSON.parse(initialContent);
                const formattedContent = {};
                Object.entries(parsedContent).forEach(([lang, data]) => {
                    formattedContent[lang] = data.content || '';
                    const textLength = (data.content || '').length;
                    handleFieldUpdate(setCharCount, lang, textLength);
                });
                setContent(formattedContent);
            } catch (e) {
                console.error('Failed to parse initial content:', e);
            }
        }
    }, [initialContent]);

    useEffect(() => {
        // Vérifier si un des contenus dépasse la limite
        const isValid = Object.values(charCount).every(count => count <= SMS_CHAR_LIMIT);
        if (onValidityChange) {
            onValidityChange(isValid);
        }
    }, [charCount, onValidityChange]);

    const handleFieldUpdate = (setter, language, value) => {
        setter(prevState => ({
            ...prevState,
            [language]: value
        }));
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const languageEntries = Object.entries(langs);

    const calculateSmsCredits = (charCount) => {
        if (charCount <= 160) return 1;
        return Math.ceil((charCount) / 153);
    };

    const handleTextChange = (language) => (event) => {
        const newContent = event.target.value;
        handleFieldUpdate(setContent, language, newContent);
        handleFieldUpdate(setCharCount, language, newContent.length);
    };

    useImperativeHandle(ref, () => ({
        getContent: () => {
            return Object.keys(langs).reduce((formattedContent, language) => {
                formattedContent[language] = {
                    content: content[language]
                };
                return formattedContent;
            }, {});
        }
    }));

    return (
        <Box>
            <Tabs
                value={currentTab}
                onChange={handleTabChange}
            >
                {languageEntries.map(([langCode, langLabel], index) => (
                    <Tab key={langCode} label={t('lang.' + langCode)} />
                ))}
            </Tabs>

            <MergeFieldHelp />

            {languageEntries.map(([langCode, langLabel], index) => (
                <Box key={langCode} hidden={currentTab !== index} sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={10}
                        value={content[langCode]}
                        onChange={handleTextChange(langCode)}
                        inputProps={{
                            lang: langCode,
                        }}
                    />
                    <Box sx={{ 
                        mt: 1, 
                        color: charCount[langCode] > SMS_CHAR_LIMIT ? 'error.main' : 'text.secondary',
                        fontSize: '0.875rem'
                    }}>
                        {charCount[langCode] > 1 
                            ? t('{{count}} characters', { count: charCount[langCode] || 0 })
                            : t('{{count}} character', { count: charCount[langCode] || 0 })}
                        {charCount[langCode] <= SMS_CHAR_LIMIT && charCount[langCode] > 0 && (
                            <span> - {calculateSmsCredits(charCount[langCode]) > 1
                                ? t('{{credits}} SMS credits', { 
                                    credits: calculateSmsCredits(charCount[langCode])
                                })
                                : t('{{credits}} SMS credit', { 
                                    credits: calculateSmsCredits(charCount[langCode])
                                })}</span>
                        )}
                        {charCount[langCode] > SMS_CHAR_LIMIT && (
                            <span> - {t('Character limit exceeded')}</span>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
});

export default SmsContent; 